/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Button } from '@mui/material';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import { IconButton } from 'components/buttons/IconSVG';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { getExcelFileName } from 'utils/ExcelUtil';
import { Code } from 'models/common/CommonCode';
import { SearchParam, UtMatrixHookupTag } from 'models/ut/UtMatrixHookupTag';
import { getUtMatrixHookupTagList } from 'apis/ut/UtMatrixHookupTag';
import { findWithConditionMaster } from 'apis/ut/UtMatrixRegist';
import { UtMatrixMaster } from 'models/ut/UtMatrix';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { CommonUtil } from 'utils/CommonUtil';
import GridSettingButtons from '../../components/grids/GridSettingButtons';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';
import { addClass, DataType, toggleClass } from '@grapecity/wijmo';
import { ChatbotButton } from 'components/buttons/ChatbotButton';
import { useChatbotStore } from 'stores/useChatbotStore';

/**
 * @desc UT Matrix Hookup Tag List 페이지
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.03.25   jihwan89       CSR E20260318-001141 기계 리스트에 사용량 추가
 * 2026.06.17   jihwan89       조회 조건 (버전, 기계/전기) 및 컬럼 수정(버전 등)
 * 2026.10.20   jihwan89       CSR E20260921-000266 차단기 용량 추가
 */

const UtMatrixHookupTagPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const gridRef = useRef<any>();
  const gridRef2 = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);
  const [rowData, setRowData] = useState<UtMatrixHookupTag[]>([]);
  const [masterData, setMasterData] = useState<UtMatrixMaster[]>([]);
  const [processData, setProcessData] = useState<UtMatrixHookupTag[]>([]);
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [tagDivsCode, setTagDivsCode] = useState<Code[]>([]);
  const [errors, setErrors] = useState<any>({});
  const openChatbot = useChatbotStore((s) => s.openPanel);

  const initSearchParam = {
    utmId: '',
    utmNo: '',
    utmNm: '',
    bildPlntCds: [],
    prdnProcCds: [],
    eqclIds: [],
    dtalProcCds: [],
    eqpMchId: '',
    tadDivsCds: [],
    verNos: [],
    type: 'HOOKUP',
  };
  const [searchParam, setSearchParam] = useState<SearchParam>(initSearchParam);

  // 검색조건 > UT Matrix 명 (ComboBox 사용을 위해 변환)
  const optionsUtMaster = useMemo(() => {
    return (masterData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.utmNo,
          cmnCdNm: cur.utmNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [masterData]);

  const selectedMaster = useMemo(() => {
    if (!searchParam?.utmNo) return null;
    const master = (masterData || []).filter((o) => o.utmNo === searchParam?.utmNo);
    return master.length > 0 ? master[0] : null;
  }, [searchParam?.utmNo, masterData]);

  // 검색조건 > 공정 (ComboBox 사용을 위해 변환)
  const optionsProcess = useMemo(() => {
    return (processData || []).reduce((acc, cur) => {
      if (acc.findIndex(({ cmnCd }) => cmnCd === cur.prdnProcCd) < 0) {
        acc.push({
          cmnCd: cur.prdnProcCd,
          cmnCdNm: cur.prdnProcCdNm,
        });
      }
      return acc;
    }, [] as Code[]);
  }, [processData]);

  // 검색조건 > 설비군 (ComboBox 사용을 위해 변환)
  const optionsEqclId = useMemo(() => {
    if ((searchParam?.prdnProcCds || []).length < 1) return [];
    return (processData || []).reduce((acc, cur) => {
      if ((searchParam?.prdnProcCds || []).includes(cur.prdnProcCd)) {
        if (acc.findIndex(({ cmnCd }) => cmnCd === cur.eqclId) < 0) {
          acc.push({
            cmnCd: cur.eqclId,
            cmnCdNm: cur.eqclIdNm,
          });
        }
      }
      return acc;
    }, [] as Code[]);
  }, [processData, searchParam?.prdnProcCds]);

  // 검색조건 > 세부공정 (ComboBox 사용을 위해 변환)
  const optionDtlProcess = useMemo(() => {
    if ((searchParam?.prdnProcCds || []).length < 1) return [];
    return (processData || []).reduce((acc, cur) => {
      if (
        (searchParam?.prdnProcCds || []).includes(cur.prdnProcCd) &&
        (searchParam?.eqclIds || []).includes(cur.eqclId)
      ) {
        if (acc.findIndex(({ cmnCd }) => cmnCd === cur.dtalProcCd) < 0) {
          acc.push({
            cmnCd: cur.dtalProcCd,
            cmnCdNm: cur.dtalProcCdNm,
          });
        }
      }
      return acc;
    }, [] as Code[]);
  }, [processData, searchParam?.prdnProcCds, searchParam?.eqclIds]);

  // 검색조건 > 세부공정 (ComboBox 사용을 위해 변환)
  const optionVerNoProcess = useMemo(() => {
    if (_.isEmpty(searchParam.utmNo)) return [];
    return processData.reduce((acc, cur) => {
      if (cur.utmNo === searchParam.utmNo) {
        const isDuplicate = acc.some((item) => item.cmnCd === cur.verNo);
        if (!isDuplicate) {
          acc.push({
            cmnCd: cur?.verNo,
            cmnCdNm: cur?.verNo,
          });
        }
      }
      const sort = acc.sort((a, b) =>
        (b.cmnCd || '').localeCompare(a.cmnCd || '', [], { numeric: true })
      );
      setSearchParam({ ...searchParam, verNos: [sort[0]?.cmnCd || ''] });
      return sort;
    }, [] as Code[]); // 초기값은 빈 배열
  }, [processData, searchParam?.utmId]);

  useEffect(() => {
    initCondition();
  }, []);

  const initCondition = async () => {
    const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
    const tagDivsCode = await getCommonCodeNames('TAG_DIVS_CD');
    setBildPlntCode(bildPlntCode);
    setTagDivsCode(tagDivsCode);
  };

  useEffect(() => {
    setSearchParam((prev) => ({
      ...prev,
      prdnProcCds: [],
      eqclIds: [],
      dtalProcCds: [],
    }));
    if (!searchParam?.utmNo) {
      setProcessData([]);
      return;
    }
    getUtMatrixHookupTagList({ utmNo: searchParam?.utmNo }).then((result: UtMatrixHookupTag[]) => {
      if (result !== null) {
        setProcessData(result);
      }
    });
  }, [searchParam?.utmNo]);

  useEffect(() => {
    if (_.isEmpty(searchParam?.bildPlntCds)) {
      setMasterData([]);
      return;
    }

    findWithConditionMaster(searchParam).then((result: UtMatrixMaster[]) => {
      setMasterData(result);
    });
  }, [searchParam?.bildPlntCds]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'utmNo',
        header: String(t('ut.label.UT Matrix 번호', 'UT Matrix 번호')),
        align: 'center',
        width: 140,
      },
      {
        binding: 'verNo',
        header: String(t('ut.label.버전', '버전')),
        align: 'center',
        width: 80,
      },
      {
        binding: 'utmNm',
        header: String(t('ut.label.UT Matrix 명', 'UT Matrix 명')),
        width: 200,
      },
      {
        binding: 'tagDivsCdNm',
        header: String(t('ut.label.기계/전기', '기계/전기')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'tagId',
        header: String(t('ut.label.Tag ID', 'Tag ID')),
        width: 250,
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('ut.label.공정', '공정')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'eqclIdNm',
        header: String(t('ut.label.설비군', '설비군')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'dtalProcCdNm',
        header: String(t('ut.label.세부공정', '세부공정')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'eqpMainNm',
        header: String(t('ut.label.Main', 'Main')),
        width: 110,
      },
      {
        binding: 'eqpMchNm',
        header: String(t('ut.label.Machine', 'Machine')),
        width: 140,
      },
      {
        binding: 'stndEqpId',
        header: String(t('ut.label.표준설비코드', '표준설비코드')),
        width: 150,
        align: 'left',
      },
      {
        binding: 'bizAreaNm',
        header: String(t('ut.label.Area', 'Area')),
        width: 80,
        align: 'center',
      },
      {
        binding: 'bldgFloCdNm',
        header: String(t('ut.label.Floor', 'Floor')),
        width: 70,
        align: 'center',
      },
      {
        binding: 'prdnLnNm',
        header: String(t('ut.label.Line', 'Line')),
        width: 70,
        align: 'right',
      },
      {
        binding: 'prdnPrlLnNm',
        header: String(t('ut.label.Sub Line', 'Sub Line')),
        width: 90,
        align: 'right',
      },
      {
        binding: 'utCode',
        header: String(t('ut.label.UT 항목', 'UT 항목')),
        width: 90,
        align: 'center',
      },
      {
        binding: 'brkeCapa',
        header: String(t('ut.label.차단기용량', '차단기용량')),
        width: 120,
        align: 'right',
        cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      },
      {
        binding: 'eqpQty',
        header: String(t('ut.label.수량', '수량')),
        width: 90,
        align: 'right',
      },
      {
        binding: 'utCspSumCapa',
        header: String(t('ut.label.사용량', '사용량')),
        width: 90,
        align: 'right',
        dataType: DataType.Number,
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'utUnit',
        header: String(t('ut.label.단위', '단위')),
        width: 90,
        align: 'center',
      },
      // {
      //   binding: 'vltgNvl',
      //   header: String(t('ut.label.Voltage', 'Voltage')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'elpwPhasCd',
      //   header: String(t('ut.label.Phase', 'Phase')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'ctwTpCd',
      //   header: String(t('ut.label.Wire', 'Wire')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'od1EcNvl',
      //   header: String(t('ut.label.Current(입력값)', 'Current(입력값)')),
      //   width: 120,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'brkeCapa',
      //   header: String(t('ut.label.Breaker(MCCB)', 'Breaker(MCCB)')),
      //   width: 120,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'elpwCapa',
      //   header: String(t('ut.label.Electricity Capacity', 'Electricity Capacity')),
      //   width: 120,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'cdaDevcCnctTpCdNm',
      //   header: String(t('ut.label.Connection[Type]', 'Connection[Type]')),
      //   width: 140,
      //   align: 'center',
      // },
      // {
      //   header: String(t('ut.label.CDA', 'CDA')),
      //   columns: [
      //     {
      //       binding: 'cdaPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'cdaCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.N2', 'N2')),
      //   columns: [
      //     {
      //       binding: 'nitrPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'nitrCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.IW', 'IW')),
      //   columns: [
      //     {
      //       binding: 'iwPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'iwCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.FW', 'FW')),
      //   columns: [
      //     {
      //       binding: 'frwtPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'frwtCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.PCW', 'PCW')),
      //   columns: [
      //     {
      //       binding: 'coolPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'coolCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.WW', 'WW')),
      //   columns: [
      //     {
      //       binding: 'wwPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'wwCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.Steam', 'Steam')),
      //   columns: [
      //     {
      //       binding: 'stemPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'stemCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.NG', 'NG')),
      //   columns: [
      //     {
      //       binding: 'lngPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'lngCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.Return Air', 'Return Air')),
      //   columns: [
      //     {
      //       binding: 'insdExhaPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'insdExhaCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.Exhaust Air', 'Exhaust Air')),
      //   columns: [
      //     {
      //       binding: 'otsdExhaPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'otsdExhaCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   header: String(t('ut.label.Supply Air', 'Supply Air')),
      //   columns: [
      //     {
      //       binding: 'suarPntCnt',
      //       header: String(t('ut.label.Qty', 'Qty')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: (params) =>
      //         `${params.value ? Number(params.value).toLocaleString() : ''}`,
      //     },
      //     {
      //       binding: 'suarCspSumCapa',
      //       header: String(t('ut.label.사용량', '사용량')),
      //       width: 80,
      //       align: 'right',
      //       cellTemplate: DefaultZeroNumberCellTemplate,
      //     },
      //   ],
      // },
      // {
      //   binding: 'cdaPntCnt',
      //   header: String(t('ut.label.CDA(Qty)', 'CDA(Qty)')),
      //   width: 100,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'nitrPntCnt',
      //   header: String(t('ut.label.N2(Qty)', 'N2(Qty)')),
      //   width: 100,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'iwPntCnt',
      //   header: String(t('ut.label.IW(Qty)', 'IW(Qty)')),
      //   width: 100,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'frwtPntCnt',
      //   header: String(t('ut.label.FW(Qty)', 'FW(Qty)')),
      //   width: 100,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'coolPntCnt',
      //   header: String(t('ut.label.PCW(Qty)', 'PCW(Qty)')),
      //   width: 100,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'wwPntCnt',
      //   header: String(t('ut.label.WW(Qty)', 'WW(Qty)')),
      //   width: 100,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'stemPntCnt',
      //   header: String(t('ut.label.Steam(Qty)', 'Steam(Qty)')),
      //   width: 100,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'lngPntCnt',
      //   header: String(t('ut.label.NG(Qty)', 'NG(Qty)')),
      //   width: 100,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'insdExhaPntCnt',
      //   header: String(t('ut.label.Retrun Air(Qty)', 'Retrun Air(Qty)')),
      //   width: 120,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'otsdExhaPntCnt',
      //   header: String(t('ut.label.Exhaust Air(Qty)', 'Exhaust Air(Qty)')),
      //   width: 120,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      // {
      //   binding: 'suarPntCnt',
      //   header: String(t('ut.label.Supply Air(Qty)', 'Supply air(Qty)')),
      //   width: 120,
      //   align: 'right',
      //   cellTemplate: (params) => `${params.value ? Number(params.value).toLocaleString() : ''}`,
      // },
      {
        binding: 'dataInsUserNm',
        header: String(t('ut.label.등록자', '등록자')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('ut.label.등록일', '등록일')),
        width: 100,
        align: 'center',
      },
    ];
  }, [rowData, isChangeColumn, flexRef]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
    setFlexRef(gridRef);
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });

      grid.invalidate();
    });
  };

  const handleOnChange = (name, value) => {
    setSearchParam({ ...searchParam, [name]: value });
    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = 'Hookup tagging list';
    book.saveAsync(getExcelFileName(t('ut.label.Hookup Tagging List', 'Hookup Tagging List')));
  };

  const btnSearch = () => {
    if (_.isEmpty(searchParam.utmNo) || _.isEmpty(searchParam.bildPlntCds)) {
      setErrors((prev) => ({
        ...prev,
        utmNo: _.isEmpty(searchParam.utmNo) ? true : false,
        bildPlntCds: _.isEmpty(searchParam.bildPlntCds) ? true : false,
      }));
      return;
    }
    getUtMatrixHookupTagList(searchParam).then((result: UtMatrixHookupTag[]) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  return (
    <ContainerLayout>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>
                  <span className={'dot'}>{t('ut.label.공장', '공장')}</span>
                </label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.bildPlntCds || []}
                  isError={errors?.bildPlntCds}
                  msgError={String(t('ut.label.공장을 선택해 주세요.', '공장을 선택해 주세요.'))}
                  onChange={(values: string[]) => {
                    const isChanged = !(
                      !!selectedMaster?.bildPlntCd &&
                      (values || []).includes(selectedMaster?.bildPlntCd)
                    );
                    setErrors({ ...errors, bildPlntCds: false });
                    setSearchParam((prev) => {
                      return {
                        ...prev,
                        bildPlntCds: values,
                        utmId: isChanged ? '' : prev?.utmId,
                        prdnProcCds: isChanged ? [] : prev?.prdnProcCds,
                        eqclIds: isChanged ? [] : prev?.eqclIds,
                        dtalProcCds: isChanged ? [] : prev?.dtalProcCds,
                        utmNo: '',
                        verNos: [],
                      };
                    });
                  }}
                  className="width25"
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span className={'dot'}>{t('ut.label.UT Matrix 명', 'UT Matrix 명')}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsUtMaster}
                  defaultValue={searchParam?.utmNo}
                  isError={errors?.utmNo}
                  msgError={String(
                    t('ut.label.UT Matrix 명을 선택해 주세요.', 'UT Matrix 명을 선택해 주세요.')
                  )}
                  onChange={(value) => {
                    setErrors({ ...errors, utmNo: false });
                    setSearchParam((prev) => ({
                      ...prev,
                      utmNo: value,
                      prdnProcCds: [],
                      eqclIds: [],
                      dtalProcCds: [],
                      verNos: [],
                    }));
                  }}
                />
              </SearchCols>
              {/* <SearchCols>
                <label>{t('ut.label.UT Matrix 번호', 'UT Matrix 번호')}</label>
                <CustomInputWithSearch
                  name="utmNo"
                  value={selectedMaster?.utmNo || ''}
                  readOnly={true}
                  isWrap={false}
                />
              </SearchCols> */}
              <SearchCols>
                <label>{String(t('ut.label.버전', '버전'))}</label>
                <MultiComboBox
                  id="verNo"
                  options={optionVerNoProcess}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.verNos || []}
                  onChange={(values) => {
                    setSearchParam((prev) => {
                      return {
                        ...prev,
                        verNos: values,
                      };
                    });
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('ut.label.기계/전기', '기계/전기'))}</label>
                <MultiComboBox
                  id="tagDivsCd"
                  options={tagDivsCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.tagDivsCds || []}
                  onChange={(values) => {
                    setSearchParam((prev) => {
                      return {
                        ...prev,
                        tagDivsCds: values,
                      };
                    });
                  }}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('ut.label.공정', '공정'))}</label>
                <MultiComboBox
                  id="prdnProcCd"
                  options={optionsProcess}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.prdnProcCds || []}
                  onChange={(values) => {
                    setSearchParam((prev) => {
                      return {
                        ...prev,
                        prdnProcCds: values,
                        eqclIds: [],
                        dtalProcCds: [],
                      };
                    });
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('ut.label.설비군', '설비군'))}</label>
                <MultiComboBox
                  id="eqpGrcd"
                  options={optionsEqclId}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.eqclIds || []}
                  onChange={(values) => {
                    setSearchParam((prev) => ({
                      ...prev,
                      eqclIds: values,
                      dtalProcCds: [],
                    }));
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('ut.label.세부공정', '세부공정'))}</label>
                <MultiComboBox
                  id="dtalProcCode"
                  options={optionDtlProcess}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.dtalProcCds || []}
                  onChange={(values) => {
                    setSearchParam((prev) => ({
                      ...prev,
                      dtalProcCds: values,
                    }));
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.Machine', 'Machine')}</label>
                <CustomInputWithSearch
                  name="eqpMchNm"
                  value={searchParam.eqpMchNm || ''}
                  defaultValue={searchParam.eqpMchNm || ''}
                  placeholder={String(
                    t('com.label.Machine을 입력해 주세요.', 'Machine을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={initSearchParam}
            setReset={setSearchParam}
            onSearch={btnSearch}
          />
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.Hookup Tagging List', 'Hookup Tagging List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'hookupTagListGrid'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
              gridRef.current.refresh();
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'hookupTagListGrid'}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        height={590}
        isSelector={false}
        isReadOnly={true}
        isFilter={true}
        excludePin={[
          'no',
          'utmNo',
          'utmNm',
          'tagDivsCdNm',
          'tagId',
          'prdnProcCdNm',
          'eqclIdNm',
          'dtalProcCdNm',
          'eqpMainNm',
          'eqpMchNm',
          'stndEqpId',
          'bizAreaNm',
          'bldgFloCdNm',
          'prdnLnNm',
          'prdnPrlLnNm',
          'machEqpQty',
          'elpwEqpQty',
          'vltgNvl',
          'elpwPhasCd',
          'ctwTpCd',
          'od1EcNvl',
          'elpwCapa',
          'cdaDevcCnctTpCdNm',
          'cdaPntCnt',
          'nitrPntCnt',
          'iwPntCnt',
          'frwtPntCnt',
          'coolPntCnt',
          'wwPntCnt',
          'stemPntCnt',
          'lngPntCnt',
          'insdExhaPntCnt',
          'otsdExhaPntCnt',
          'vacmPumpPntCnt',
          'suarPntCnt',
          'dataInsUserNm',
          'dataInsDtm',
        ]}
      />
      <ChatbotButton onClick={() => openChatbot('ut-matrix')} />
    </ContainerLayout>
  );
};

export default UtMatrixHookupTagPage;
